/**
 * Document-title state suffix.
 *
 * A page whose useful state belongs in the browser tab title (screen readers
 * announce the title on navigation; users find the tab) calls
 *
 *     useDocumentTitleState(t("meta.states.keyNeeded"))
 *
 * and `RouteDocumentMeta` renders
 *
 *     {page} - {suffix} | ProperHumanDiet        (e.g. "Voice Log - key needed | ProperHumanDiet")
 *
 * API
 * ---
 * `useDocumentTitleState(suffix: string | null | undefined): void`
 *     - `suffix` is ALREADY LOCALIZED text (translate at the call site).
 *     - null / undefined / "" / whitespace clears it (use while loading, or
 *       when the page is in its normal state, so the title is just the page).
 *     - Cleared automatically on unmount and on route change (the page
 *       unmounts), so a stale "key needed" can never leak onto another route.
 *     - The brand ("ProperHumanDiet") is never part of the suffix and is not
 *       translated.
 *
 * `useDocumentTitleStateValue(): string | null`
 *     - Subscribes to the current suffix. Used by `RouteDocumentMeta`.
 *
 * Common-namespace strings already exist for the Voice Log states:
 *     meta.states.keyNeeded   -> "key needed"
 *     meta.states.keyFailing  -> "key not working"
 *
 * Implemented as a tiny external store (no provider) because the page and
 * `RouteDocumentMeta` are siblings under the shell, not ancestor/descendant.
 */
import { useEffect, useRef, useSyncExternalStore } from "react";

let current: string | null = null;
/** Owner token so an unmounting page only clears what it set. */
let owner: symbol | null = null;
const listeners = new Set<() => void>();

function emit() {
  listeners.forEach((l) => l());
}

function normalize(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot(): string | null {
  return current;
}

export function useDocumentTitleState(
  suffix: string | null | undefined,
): void {
  const value = normalize(suffix);
  const tokenRef = useRef<symbol | null>(null);
  if (tokenRef.current === null) tokenRef.current = Symbol("document-title-state");

  // Publish on every value change (no cleanup here, so changing the suffix
  // never flashes an intermediate "no suffix" title).
  useEffect(() => {
    owner = tokenRef.current;
    if (current !== value) {
      current = value;
      emit();
    }
  }, [value]);

  // Clear only on unmount, and only if a newer page has not taken over.
  useEffect(() => {
    const token = tokenRef.current;
    return () => {
      if (owner === token) {
        owner = null;
        if (current !== null) {
          current = null;
          emit();
        }
      }
    };
  }, []);
}

export function useDocumentTitleStateValue(): string | null {
  return useSyncExternalStore(subscribe, getSnapshot, () => null);
}

/** Test helper: reset the store between tests. */
export function __resetDocumentTitleStateForTests(): void {
  current = null;
  owner = null;
  listeners.clear();
}
